import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import FieldRow from './FieldRow';
import LocalizedField from './LocalizedField';
import SectionBlock from './SectionBlock';
import { getContentEventTypes, getPlatformTypes, getEventFormats } from '../../../api/eventApi';

const formatDateTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

// Resolves a lookup id to its display name; falls back to the raw id (never blank) if the
// lookup hasn't loaded yet or the value no longer matches anything (e.g. a deleted type).
const nameOrId = (list, id, nameKey) => {
  if (id == null) return null;
  const match = list.find((item) => String(item.id) === String(id));
  return match ? match[nameKey] : id;
};

export default function EventDetailPanel({ data, diffKeys = new Set(), side }) {
  const [eventTypes, setEventTypes] = useState([]);
  const [platformTypes, setPlatformTypes] = useState([]);
  const [eventFormats, setEventFormats] = useState([]);

  useEffect(() => {
    getContentEventTypes().then(setEventTypes).catch(() => setEventTypes([]));
    getPlatformTypes().then(setPlatformTypes).catch(() => setPlatformTypes([]));
    getEventFormats().then(setEventFormats).catch(() => setEventFormats([]));
  }, []);

  if (!data) return null;

  return (
    <Box>
      <SectionBlock title="Identity" />
      <LocalizedField
        label="Name"
        fieldKey="localizedName"
        data={data.localizedName}
        diffKeys={diffKeys}
        side={side}
      />
      <LocalizedField
        label="Description"
        fieldKey="localizedDescription"
        data={data.localizedDescription}
        diffKeys={diffKeys}
        side={side}
      />

      <SectionBlock title="Classification" />
      <FieldRow
        label="Event Type"
        value={nameOrId(eventTypes, data.eventTypeId, 'Type')}
        isChanged={diffKeys.has('eventTypeId')}
        side={side}
      />
      <FieldRow
        label="Platform Type"
        value={nameOrId(platformTypes, data.platformTypeId, 'PlatformName')}
        isChanged={diffKeys.has('platformTypeId')}
        side={side}
      />
      <FieldRow
        label="Format"
        value={nameOrId(eventFormats, data.eventFormatId, 'Name')}
        isChanged={diffKeys.has('eventFormatId')}
        side={side}
      />
      <FieldRow
        label="Personality"
        value={data.personalityName ?? data.personalityId}
        isChanged={diffKeys.has('personalityId')}
        side={side}
      />
      {data.personalityPhotoLink && (
        <Box sx={{ px: 2, pb: 1.25 }}>
          <Box
            component="img"
            src={data.personalityPhotoLink}
            alt={data.personalityName || 'Personality'}
            sx={{ width: 64, height: 64, borderRadius: '8px', objectFit: 'cover', border: '1px solid #e5e7eb' }}
          />
        </Box>
      )}
      <FieldRow label="Event URL" value={data.eventUrl} isChanged={diffKeys.has('eventUrl')} side={side} />

      <SectionBlock title="Schedule" />
      <FieldRow
        label="Start"
        value={formatDateTime(data.scheduledStart)}
        isChanged={diffKeys.has('scheduledStart')}
        side={side}
      />
      <FieldRow
        label="End"
        value={formatDateTime(data.scheduledEnd)}
        isChanged={diffKeys.has('scheduledEnd')}
        side={side}
      />
      <FieldRow
        label="Length (min)"
        value={data.scheduledLength}
        isChanged={diffKeys.has('scheduledLength')}
        side={side}
      />
      <FieldRow
        label="Live / Recorded"
        value={data.isLive == null ? null : (data.isLive ? 'Live' : 'Recorded')}
        isChanged={diffKeys.has('isLive')}
        side={side}
      />

      <SectionBlock title="Capacity" />
      <FieldRow
        label="Capacity"
        value={data.capacity == null ? 'Unlimited' : data.capacity}
        isChanged={diffKeys.has('capacity')}
        side={side}
      />

      <SectionBlock title="Hosting" />
      <FieldRow
        label="Stream URL"
        value={data.hostingStreamUrl}
        isChanged={diffKeys.has('hostingStreamUrl')}
        side={side}
      />
      <FieldRow
        label="Subtitles"
        value={data.subtitleEnabled == null ? null : (data.subtitleEnabled ? 'Enabled' : 'Disabled')}
        isChanged={diffKeys.has('subtitleEnabled')}
        side={side}
      />
    </Box>
  );
}
